import React, { useRef } from 'react';
import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    TextField,
    Grid,
    Stepper,
    Step,
    StepLabel,
    DialogActions,
    FormControl,
    Checkbox,
    Typography,
    Chip,
    FormHelperText,
    CircularProgress,
    Box,
    InputAdornment,
    IconButton,
    Switch,
    FormControlLabel,
} from '@mui/material';
import { Formik } from "formik";
import { Loadable, Message } from '../../../common';
import { orderBy, debounce } from "lodash";
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { searchFeeSchedulesFromClient } from '../../../store/api/feeScheduleV2';
import { useEffect } from 'react';
import { useState } from 'react';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { FeeScheduleSelection } from "../../feeSchedulePage/feeScheduleSelectionComponent";
import StateDropdown from '../RulesEditPage/components/StateDropdown';
import LocalityDropdown from '../RulesEditPage/components/LocalityDropdown';
import GPCIDropdown from '../RulesEditPage/components/GPCIDropdown';
import { fetchGPCILocalityData, GPCILocalityData } from '../../../store/api/globalFeeSchedule';
import { copyContractConstants } from '../../../store/constants/pages';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { mapPaymentFeeScheduleRules,useStyles,groupFeeSchedules,mapFeeScheduleRules,groupPaymentFeeSchedules,groupLesserOfConfigurations,mapLesserOfRules, groupProviderRoleConfigurations, mapProviderRoleRules } from './CopyContractUtils';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

import BasicDetailsStep from './components/BasicDetailsStep';
import FeeScheduleMappingStep from './components/FeeScheduleMappingStep';
import PaymentFeeScheduleMappingStep from './components/PaymentFeeScheduleMappingStep';
import LesserOfMappingStep from './components/LesserOfMappingStep';
import ProviderRoleMappingStep from './components/ProviderRoleMappingStep';
import CodeServiceMappingStep from './components/CodeServiceMappingStep';
import NPIRoleMappingStep from './components/NPIRoleMappingStep';
import { infoToastify } from '../../../util/toast';

export default function CopyContractDialog({
    clients, selectedClient, selectedClientSchema,
    open, handleRefresh, initialValues, copyContractPending, isSuperAdmin, hasRulesApiResponded, feeSchedulesFromClient, codeServicesFromClient,
    contractId, contractName, stateId, intl, feeScheduleRules, feeScheduleRulesGroups, lesserOfRules, providerRoleRules, codeServiceRules, getFeeSchdulePending, getRulesPending, getCodeServicePending, facilitiesFromTargetClientPending, facilitiesFromTargetClient, feeScheduleLabelsValidate,
    getFeeScheduleLabelsFromTargetClientsPending, getNPIRolesFromContractPending, getNPIRolesFromClientPending, npiRolesFromContract, npiRolesFromClient,
    actions: { copyContract, getRulesByContractId, getFeeScheduleFromClient, resetRulesByContractId, getServicesFromTargetClient, setCopyContractName, getFacilitiesByTargetClient, copyContractReset, setCopyContractDialog,
        getFeeScheduleLabelsFromTargetClient, validateLabelsFromTargetClient, clientListTiny, getNPIRolesFromContract, getNPIRolesFromTargetClient
    }
}) {
    const [gpciLocalityData, setGpciLocalityData] = useState<GPCILocalityData[]>([]);

    useEffect(() => {
        fetchGPCILocalityData()
            .then(data => setGpciLocalityData(data))
            .catch(err => console.error('Failed to load regional data', err));
    }, []);

    const [targetClient, setTargetClient] = useState(null);
    const [activeStep, setActiveStep] = useState(0);
    const [steps, setSteps] = useState([]);
    const [feeScheduleLabels, setFeeScheduleLabels] = useState({});

    const [nameExistErrors, setNameExistErrors] = useState({});
    const [nameValidatePending, setNameValidatePending] = useState({});
    
    const [copyModeToggles, setCopyModeToggles] = useState({});


    const validateDuplications = async(name, schema, type, index) => {
        if (!name || !schema) {
                setNameExistErrors(prev => ({ ...prev, [type + '-' + index]: null }));
                return;
            }

            setNameValidatePending(prev => ({ ...prev, [type + '-' + index]: true }));
            try {
                const results = await searchFeeSchedulesFromClient({ keyword: name }, schema);
                const exists = results.some(item => !item.isDirectory && item.name.toLowerCase() === name.trim().toLowerCase());
                setNameExistErrors(prev => ({
                    ...prev,
                    [type + '-' + index]: exists ? "This Fee Schedule already exists in Target client" : null
                }));
            } catch (e) {
                console.error(e);
            } finally {
                setNameValidatePending(prev => ({ ...prev, [type + '-' + index]: false }));
            }
        
    }

    const validateFeeScheduleNameExistence = useRef(
        debounce(async (name, schema, type, index) => {
            validateDuplications(name, schema, type, index)
        }, 800)
    ).current;

    const formikRef = useRef<any>();

    const classes = useStyles();

    useEffect(() => {
        setSteps([{ id: 1, label: "Basic Details" }])
        if (hasRulesApiResponded && !getNPIRolesFromContractPending) {
            if (feeScheduleRules.length === 0 && feeScheduleRulesGroups.length === 0 && lesserOfRules.length === 0 && codeServiceRules.length === 0 && providerRoleRules.length === 0) {
                
            } else if(formikRef?.current) {    

                const feeScheduleMapping = mapFeeScheduleRules(groupFeeSchedules(feeScheduleRules));
                formikRef.current.setFieldValue('feeScheduleMapping', feeScheduleMapping);

                const codeServiceMapping = codeServiceRules.map(rule => ({
                        ruleId: rule.id,
                        ruleName: rule.name,
                        codeServiceName: rule?.codeService?.name,
                        sourceCodeServiceId: rule?.codeService?.id,
                        targetCodeServiceId: { id: 0, name: 'Copy Code Service' },
                        newCodeServiceName: ""
                    }));
                formikRef.current.setFieldValue('codeServiceMapping', codeServiceMapping);

                // Initialize payment fee schedule mappings
                const paymentFeeScheduleMapping = mapPaymentFeeScheduleRules(groupPaymentFeeSchedules(feeScheduleRulesGroups));
                formikRef.current.setFieldValue('paymentFeeScheduleMapping', paymentFeeScheduleMapping);

                // Initialize lesser of mappings
                const lesserOfMapping = mapLesserOfRules(groupLesserOfConfigurations(lesserOfRules));
                formikRef.current.setFieldValue('lesserOfMapping', lesserOfMapping);

                // Initialize Provider Role mappings
                const providerRoleMapping = mapProviderRoleRules(groupProviderRoleConfigurations(providerRoleRules));
                formikRef.current.setFieldValue('providerRoleMapping', providerRoleMapping);

                const npiRoleMapping = npiRolesFromContract?.map(role => ({
                        sourceNpiRoleId: role.id,
                        sourceNpiRoleName: role.name,
                        targetNpiRoleId: { id: 0, name: 'Copy Provider Role' },
                        newNpiRoleName: role.name
                    })) || [];
                formikRef.current.setFieldValue('npiRoleMapping', npiRoleMapping);

                // Initialize steps based on data availability
                setSteps(prevSteps => {
                    const newSteps = [...prevSteps];
                    if (feeScheduleMapping.localFeeSchedules.length > 0 || feeScheduleMapping.globalFeeSchedules.length > 0) {
                        newSteps.push({ id: 2, label: copyContractConstants.FEESCHEDULE });
                    }
                    if (paymentFeeScheduleMapping.localFeeSchedules.length > 0 || paymentFeeScheduleMapping.globalFeeSchedules.length > 0) {
                        newSteps.push({ id: 3, label: copyContractConstants.PAYMENTFEESCHEDULE });
                    }
                    if (lesserOfMapping.localFeeSchedules.length > 0 || lesserOfMapping.globalFeeSchedules.length > 0) {
                        newSteps.push({ id: 4, label: copyContractConstants.LESSEROF });
                    }
                    if (codeServiceMapping.length > 0) {
                        newSteps.push({ id: 5, label: copyContractConstants.CODESERVICE });
                    }
                    if (npiRoleMapping.length > 0) {
                        newSteps.push({ id: 6, label: copyContractConstants.NPIROLE });
                    }
                    if (providerRoleMapping.localFeeSchedules.length > 0 || providerRoleMapping.globalFeeSchedules.length > 0) {
                        newSteps.push({ id: 7, label: copyContractConstants.PROVIDERROLE });
                    }
                    return newSteps;
                });
            }
        }
    }, [hasRulesApiResponded, feeScheduleRules, feeScheduleRulesGroups, lesserOfRules, codeServiceRules, getNPIRolesFromContractPending, npiRolesFromContract, providerRoleRules]);

    useEffect(() => {
        if (isSuperAdmin) {
            clientListTiny();
        }
        let selectedClientDef = null;
        selectedClientDef = clients.filter(function (client) { return client?.clientId === selectedClient });
        setTargetClient(selectedClientDef[0] ? selectedClientDef[0] : null);
    }, [clients.length]);

    useEffect(() => {
        if (!targetClient?.schema) return;

        // Small delay to ensure formik values are populated
        const timer = setTimeout(() => {
            if (!formikRef.current) return;
            
            const values = formikRef.current.values;
            
            if (activeStep === 1) { 

                values?.feeScheduleMapping?.localFeeSchedules?.forEach((group, index) => {
                    if (group.newFeeScheduleName) {
                        validateDuplications(group.newFeeScheduleName, targetClient.schema, 'step2-localFeeSchedules', index);
                    }
                });
            
            }

            if (activeStep === 2) { // Step 3 - Payment Fee Schedule Mapping
                values?.paymentFeeScheduleMapping?.localFeeSchedules?.forEach((group, index) => {
                    if (group.newFeeScheduleName) {
                        validateDuplications(group.newFeeScheduleName, targetClient.schema, 'step3-localFeeSchedules', index);
                    }
                });
            }

            if (activeStep === 3) { // Step 4 - Lesser of Configuration Mapping
                values?.lesserOfMapping?.localFeeSchedules?.forEach((group, index) => {
                    if (group.newFeeScheduleName) {
                        validateDuplications(group.newFeeScheduleName, targetClient.schema, 'step-lesserof-localFeeSchedules', index);
                    }
                });
            }

            if (activeStep === 7) { // Step 7 - Provider Role Mapping
                values?.providerRoleMapping?.localFeeSchedules?.forEach((group, index) => {
                    if (group.newFeeScheduleName) {
                        validateDuplications(group.newFeeScheduleName, targetClient.schema, 'step-providerrole-localFeeSchedules', index);
                    }
                });
            }
        }, 100); // Small delay to ensure formik is ready

        return () => clearTimeout(timer);
    }, [targetClient?.schema, activeStep]);


    function handleClosing(state) {
        setActiveStep(0);
        resetRulesByContractId();
        setSteps([{ id: 1, label: "Basic Details" }]);
        setCopyContractName("");

        setCopyContractDialog({ 
            open: false, 
            existingContractId: null, 
            existingContractName: '', 
            existingContractStateId: null 
        });
        copyContractReset();

        // Reset Formik state to initial values
        formikRef.current?.resetForm();

        // Clear local UI and validation states
        setNameExistErrors({});
        setNameValidatePending({});
        setCopyModeToggles({});
        setFeeScheduleLabels({});

        if (targetClient != null && targetClient?.clientId !== selectedClient) {
            let selectedClientDef = null;
            selectedClientDef = clients.filter(function (client) { return client?.clientId === selectedClient });
            setTargetClient(selectedClientDef[0] ? selectedClientDef[0] : null);
        }
    }

    function handleCallback() {
        infoToastify(<><b>Copying Contract</b><br/>Copy Contract is in Progress, You'll be notified when it's complete.</>);
        handleClosing(true);
    }

    function handleGetRulesForCrossClient(newClient) {
        resetRulesByContractId();
        setSteps([{ id: 1, label: "Basic Details" }]);

        if (selectedClient !== newClient.clientId && newClient?.schema) {
            getFacilitiesByTargetClient(newClient?.schema);
            getNPIRolesFromContract(contractId);
            getRulesByContractId(contractId);

            getFeeScheduleFromClient(newClient?.schema);
            getServicesFromTargetClient(newClient?.schema);
            getNPIRolesFromTargetClient(newClient?.clientId);
        }
    }

    function handleBack() {
        setActiveStep((prevStep) => prevStep - 1);
    }

    function handleNext() {
        setActiveStep((prevStep) => prevStep + 1);
    }

    function handleNextWithValidation(_values) {


        if (steps[activeStep]?.label===copyContractConstants.FEESCHEDULE && (
            (_values.feeScheduleMapping?.localFeeSchedules?.length > 0) || 
            (_values.feeScheduleMapping?.globalFeeSchedules?.length > 0)
        )) {
            // custom error trigger for fee schedule
            let hasError = false;
            
            const checkList = (list, pathPrefix, isGlobal = false) => {
                list?.forEach((group, groupIndex) => {
                    // Group Level Check: New Fee Schedule Name
                    const isNewFSNameRequired = !isGlobal && group.rules?.some(r => !r.targetFeeScheduleId?.id || r.targetFeeScheduleId.id === 0);
                    if (isNewFSNameRequired && (!group.newFeeScheduleName || group.newFeeScheduleName === '')) {
                         formikRef.current.setFieldError(`${pathPrefix}.${groupIndex}.newFeeScheduleName`, intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" }));
                         formikRef.current.setFieldTouched(`${pathPrefix}.${groupIndex}.newFeeScheduleName`, true);
                         hasError = true;
                    } else if (group.newFeeScheduleName && group.newFeeScheduleName.length <= 3) {
                         formikRef.current.setFieldTouched(`${pathPrefix}.${groupIndex}.newFeeScheduleName`, true);
                         hasError = true;
                    }

                    // Rule Level Checks
                    group.rules?.forEach((rule, ruleIndex) => {
                        if (isGlobal && (!rule.targetFeeScheduleId?.id || rule.targetFeeScheduleId.id === 0)) {
                             formikRef.current.setFieldTouched(`${pathPrefix}.${groupIndex}.rules.${ruleIndex}.targetFeeScheduleId`, true);
                             hasError = true;
                        }
                         if (rule.isRBRVS || rule?.isDME) {
                              if (!rule.selectedState) {
                                   formikRef.current.setFieldTouched(`${pathPrefix}.${groupIndex}.rules.${ruleIndex}.selectedState`, true);
                                   hasError = true;
                              }
                              if (!rule.selectedGpci && rule.isRBRVS) {
                                   formikRef.current.setFieldTouched(`${pathPrefix}.${groupIndex}.rules.${ruleIndex}.selectedGpci`, true);
                                   hasError = true;
                              }
                               if (!rule.selectedLocality && rule.isRBRVS) {
                                   formikRef.current.setFieldTouched(`${pathPrefix}.${groupIndex}.rules.${ruleIndex}.selectedLocality`, true);
                                   hasError = true;
                              }
                         }
                    });
                });
            };

            checkList(_values.feeScheduleMapping?.localFeeSchedules, 'feeScheduleMapping.localFeeSchedules', false);
            checkList(_values.feeScheduleMapping?.globalFeeSchedules, 'feeScheduleMapping.globalFeeSchedules', true);
            
            // Check for name existence errors for current step
            const hasNameExistErrors = Object.entries(nameExistErrors).some(([key, error]) => key.startsWith('step2-') && error !== null && error !== undefined);
            
            if (!hasError && !hasNameExistErrors) {
                handleNext();
            } else if (hasNameExistErrors) {
                // Show error message if name exists
                console.error('Cannot proceed: Fee schedule name already exists in target client');
            }
        }
        else if (steps[activeStep]?.label===copyContractConstants.PAYMENTFEESCHEDULE && (
            (_values.paymentFeeScheduleMapping?.localFeeSchedules?.length > 0) || 
            (_values.paymentFeeScheduleMapping?.globalFeeSchedules?.length > 0)
        )) {
            // custom error trigger for payment fee schedule
            let hasError = false;
            
            const checkPaymentList = (list, pathPrefix, isGlobal = false) => {
                list?.forEach((group, groupIndex) => {
                    // Group Level Check: New Fee Schedule Name
                    const isNewFSNameRequired = !isGlobal && group.rules?.some(r => !r.targetFeeScheduleId?.id || r.targetFeeScheduleId.id === 0);
                    if (isNewFSNameRequired && (!group.newFeeScheduleName || group.newFeeScheduleName === '')) {
                         formikRef.current.setFieldTouched(`${pathPrefix}.${groupIndex}.newFeeScheduleName`, true);
                         hasError = true;
                    } else if (group.newFeeScheduleName && group.newFeeScheduleName.length <= 3) {
                         formikRef.current.setFieldTouched(`${pathPrefix}.${groupIndex}.newFeeScheduleName`, true);
                         hasError = true;
                    }

                    // Rule Level Checks
                    group.rules?.forEach((rule, ruleIndex) => {
                         if (!isGlobal && !isCopyNewName(`step3-local-${groupIndex}`)) {
                              if (!rule.targetFeeScheduleId?.id || rule.targetFeeScheduleId.id === 0) {
                                   formikRef.current.setFieldTouched(`${pathPrefix}.${groupIndex}.rules.${ruleIndex}.targetFeeScheduleId`, true);
                                   hasError = true;
                              }
                         }
                         if (isGlobal && (!rule.targetFeeScheduleId?.id || rule.targetFeeScheduleId.id === 0)) {
                              formikRef.current.setFieldTouched(`${pathPrefix}.${groupIndex}.rules.${ruleIndex}.targetFeeScheduleId`, true);
                              hasError = true;
                         }
                         if (rule.targetFeeScheduleId.id !== 0 && !rule.targetLabelId) {
                             formikRef.current.setFieldTouched(`${pathPrefix}.${groupIndex}.rules.${ruleIndex}.targetLabelId`, true);
                             hasError = true;
                         }
                         if (rule.isRBRVS || rule?.isDME) {
                            if (!rule.selectedState) {
                                 formikRef.current.setFieldTouched(`${pathPrefix}.${groupIndex}.rules.${ruleIndex}.selectedState`, true);
                                 hasError = true;
                            }
                            if (!rule.selectedGpci && rule.isRBRVS) {
                                 formikRef.current.setFieldTouched(`${pathPrefix}.${groupIndex}.rules.${ruleIndex}.selectedGpci`, true);
                                 hasError = true;
                            }
                             if (!rule.selectedLocality && rule.isRBRVS) {
                                 formikRef.current.setFieldTouched(`${pathPrefix}.${groupIndex}.rules.${ruleIndex}.selectedLocality`, true);
                                 hasError = true;
                            }
                       }
                    });
                });
            };

            checkPaymentList(_values.paymentFeeScheduleMapping?.localFeeSchedules, 'paymentFeeScheduleMapping.localFeeSchedules', false);
            checkPaymentList(_values.paymentFeeScheduleMapping?.globalFeeSchedules, 'paymentFeeScheduleMapping.globalFeeSchedules', true);
            
            // Check for name existence errors for current step
            const hasNameExistErrors = Object.entries(nameExistErrors).some(([key, error]) => key.startsWith('step3-') && error !== null && error !== undefined);
            
            // Check for label validation errors (cross icon)
            const hasLabelErrors = Object.entries(feeScheduleLabelsValidate).some(([key, error]) => 
                key.startsWith('done-localFeeSchedules-') && error === true
            );
            
            if (!hasError && !hasNameExistErrors && !hasLabelErrors) {
                handleNext();
            } else if (hasNameExistErrors || hasLabelErrors) {
                // Show error message if name exists or label has issues
                console.error('Cannot proceed: Validation errors exist');
            }
        }
        else if (steps[activeStep]?.label===copyContractConstants.LESSEROF && (
            (_values.lesserOfMapping?.localFeeSchedules?.length > 0) ||
            (_values.lesserOfMapping?.globalFeeSchedules?.length > 0)
        )) {
            // custom error trigger for lesser of
            let hasError = false;

            const checkLesserOfList = (list, pathPrefix, isGlobal = false) => {
                list?.forEach((group, groupIndex) => {
                    // Group Level Check: New Fee Schedule Name
                    const isToggleOn = !isGlobal && isCopyNewName(`step-lesserof-local-${groupIndex}`);
                    const isNewFSNameRequired = !isGlobal && !isToggleOn;
                    if (isToggleOn && (!group.newFeeScheduleName || group.newFeeScheduleName === '')) {
                         formikRef.current.setFieldError(`${pathPrefix}.${groupIndex}.newFeeScheduleName`, intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" }));
                         formikRef.current.setFieldTouched(`${pathPrefix}.${groupIndex}.newFeeScheduleName`, true);
                         hasError = true;
                    } else if (group.newFeeScheduleName && group.newFeeScheduleName.length <= 3) {
                         formikRef.current.setFieldTouched(`${pathPrefix}.${groupIndex}.newFeeScheduleName`, true);
                         hasError = true;
                    }

                    // Rule Level Checks
                    group.rules?.forEach((rule, ruleIndex) => {
                         if (!isToggleOn && (!rule.targetFeeScheduleId?.id || rule.targetFeeScheduleId.id === 0)) {
                              formikRef.current.setFieldTouched(`${pathPrefix}.${groupIndex}.rules.${ruleIndex}.targetFeeScheduleId`, true);
                              hasError = true;
                         }
                         if (rule.targetFeeScheduleId.id !== 0 && !rule.targetLabelId) {
                             formikRef.current.setFieldError(`${pathPrefix}.${groupIndex}.rules.${ruleIndex}.targetLabelId`, intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" }));
                             formikRef.current.setFieldTouched(`${pathPrefix}.${groupIndex}.rules.${ruleIndex}.targetLabelId`, true);
                             hasError = true;
                         }
                         if (rule.isRBRVS || rule.isDME) {
                            if (!rule.selectedState) {
                                 formikRef.current.setFieldTouched(`${pathPrefix}.${groupIndex}.rules.${ruleIndex}.selectedState`, true);
                                 hasError = true;
                            }
                            if (!rule.selectedGpci && rule.isRBRVS) {
                                 formikRef.current.setFieldTouched(`${pathPrefix}.${groupIndex}.rules.${ruleIndex}.selectedGpci`, true);
                                 hasError = true;
                            }
                             if (!rule.selectedLocality && rule.isRBRVS) {
                                 formikRef.current.setFieldTouched(`${pathPrefix}.${groupIndex}.rules.${ruleIndex}.selectedLocality`, true);
                                 hasError = true;
                            }
                       }
                    });
                });
            };

            checkLesserOfList(_values.lesserOfMapping?.localFeeSchedules, 'lesserOfMapping.localFeeSchedules', false);
            checkLesserOfList(_values.lesserOfMapping?.globalFeeSchedules, 'lesserOfMapping.globalFeeSchedules', true);

            // Check for name existence errors
            const hasNameExistErrors = Object.entries(nameExistErrors).some(([key, error]) => key.startsWith('step-lesserof-') && error !== null && error !== undefined);

            // Check for label validation errors (cross icon)
            const hasLabelErrors = Object.entries(feeScheduleLabelsValidate).some(([key, error]) => 
                key.startsWith('done-lesserof-localFeeSchedules-') && error === true
            );
            if (!hasError && !hasNameExistErrors && !hasLabelErrors) {
                handleNext();
            } else if (hasNameExistErrors || hasLabelErrors) {
                // Show error message if name exists or label has issues
                console.error('Cannot proceed: Validation errors exist');
            }
        }
        else if (steps[activeStep]?.label===copyContractConstants.CODESERVICE  && _values.codeServiceMapping.length > 0) {
            // custom error trigger for code service
            let hasError = false;
            _values.codeServiceMapping.forEach((item, index) => {
                if (item.targetCodeServiceId.id === 0 && (!item.newCodeServiceName || item.newCodeServiceName === '')) {
                    formikRef.current.setFieldTouched(`codeServiceMapping.${index}.newCodeServiceName`, true);
                    hasError = true;
                }
            });
            if (!hasError) {
                handleNext();
            }
        }
        else if (steps[activeStep]?.label===copyContractConstants.NPIROLE  && _values.npiRoleMapping.length > 0) {
            // custom error trigger for code service
            let hasError = false;
            _values.npiRoleMapping?.forEach((item, index) => {
                if (item.targetNpiRoleId.id === 0 && (!item.newNpiRoleName || item.newNpiRoleName === '')) {
                    formikRef.current.setFieldTouched(`npiRoleMapping.${index}.newNpiRoleName`, true);
                    hasError = true;
                }
            });
            if (!hasError) {
                handleNext();
            }
        }
        else if (steps[activeStep]?.label===copyContractConstants.PROVIDERROLE && (
            (_values.providerRoleMapping?.localFeeSchedules?.length > 0) ||
            (_values.providerRoleMapping?.globalFeeSchedules?.length > 0)
        )) {
            // custom error trigger for provider role
            let hasError = false;

            const checkProviderRoleList = (list, pathPrefix, isGlobal = false) => {
                list?.forEach((group, groupIndex) => {
                    // Group Level Check: New Fee Schedule Name
                    const isToggleOn = !isGlobal && isCopyNewName(`step-providerrole-local-${groupIndex}`);
                    const isNewFSNameRequired = !isGlobal && !isToggleOn;
                    if (isToggleOn && (!group.newFeeScheduleName || group.newFeeScheduleName === '')) {
                         formikRef.current.setFieldError(`${pathPrefix}.${groupIndex}.newFeeScheduleName`, intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" }));
                         formikRef.current.setFieldTouched(`${pathPrefix}.${groupIndex}.newFeeScheduleName`, true);
                         hasError = true;
                    } else if (group.newFeeScheduleName && group.newFeeScheduleName.length <= 3) {
                         formikRef.current.setFieldTouched(`${pathPrefix}.${groupIndex}.newFeeScheduleName`, true);
                         hasError = true;
                    }

                    // Rule Level Checks
                    group.rules?.forEach((rule, ruleIndex) => {
                         if (!isToggleOn && (!rule.targetFeeScheduleId?.id || rule.targetFeeScheduleId.id === 0)) {
                              formikRef.current.setFieldTouched(`${pathPrefix}.${groupIndex}.rules.${ruleIndex}.targetFeeScheduleId`, true);
                              hasError = true;
                         }
                         if (rule.targetFeeScheduleId.id !== 0 && !rule.targetLabelId) {
                             formikRef.current.setFieldError(`${pathPrefix}.${groupIndex}.rules.${ruleIndex}.targetLabelId`, intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" }));
                             formikRef.current.setFieldTouched(`${pathPrefix}.${groupIndex}.rules.${ruleIndex}.targetLabelId`, true);
                             hasError = true;
                         }
                         if (rule.isRBRVS || rule.isDME) {
                            if (!rule.selectedState) {
                                 formikRef.current.setFieldTouched(`${pathPrefix}.${groupIndex}.rules.${ruleIndex}.selectedState`, true);
                                 hasError = true;
                            }
                            if (!rule.selectedGpci && rule.isRBRVS) {
                                 formikRef.current.setFieldTouched(`${pathPrefix}.${groupIndex}.rules.${ruleIndex}.selectedGpci`, true);
                                 hasError = true;
                            }
                             if (!rule.selectedLocality && rule.isRBRVS) {
                                 formikRef.current.setFieldTouched(`${pathPrefix}.${groupIndex}.rules.${ruleIndex}.selectedLocality`, true);
                                 hasError = true;
                            }
                       }
                    });
                });
            };

            checkProviderRoleList(_values.providerRoleMapping?.localFeeSchedules, 'providerRoleMapping.localFeeSchedules', false);
            checkProviderRoleList(_values.providerRoleMapping?.globalFeeSchedules, 'providerRoleMapping.globalFeeSchedules', true);

            // Check for name existence errors
            const hasNameExistErrors = Object.entries(nameExistErrors).some(([key, error]) => key.startsWith('step-providerrole-') && error !== null && error !== undefined);

            // Check for label validation errors (cross icon)
            const hasLabelErrors = Object.entries(feeScheduleLabelsValidate).some(([key, error]) => 
                key.startsWith('done-providerrole-localFeeSchedules-') && error === true
            );
            if (!hasError && !hasNameExistErrors && !hasLabelErrors) {
                handleNext();
            } else if (hasNameExistErrors || hasLabelErrors) {
                // Show error message if name exists or label has issues
                console.error('Cannot proceed: Validation errors exist');
            }
        }
        else {
            handleNext();
        }
    }

    const handleTargetFeeScheduleChange = (setFieldValue, groupIndex, ruleIndex, value, type) => {
        setFieldValue(`feeScheduleMapping.${type}.${groupIndex}.rules.${ruleIndex}.targetFeeScheduleId`, value || { id: null, name: "" });
    }

    const handleNewFeeScheduleChange = (setFieldValue, groupIndex, value, type) => {
         setFieldValue(`feeScheduleMapping.${type}.${groupIndex}.newFeeScheduleName`, value);
         validateFeeScheduleNameExistence(value, targetClient?.schema, `step2-${type}`, groupIndex);
    }

    function handleTargetCodeServiceChange(setFieldValue, index, obj) {
        setFieldValue(`codeServiceMapping.${index}.targetCodeServiceId`, obj);
        if (obj.id === 0) {
            setFieldValue(`codeServiceMapping.${index}.newCodeServiceName`, '');
        }
    }

    function handleNewCodeServicceeChange(setFieldValue, index, val) {
        setFieldValue(`codeServiceMapping.${index}.newCodeServiceName`, val);
    }

    function handleTargetNPIRoleChange(setFieldValue, index, obj) {
        setFieldValue(`npiRoleMapping.${index}.targetNpiRoleId`, obj);
        if (obj.id === 0) {
            setFieldValue(`npiRoleMapping.${index}.newNpiRoleName`, '');
        }
    }

    function handleNewNPIRoleChange(setFieldValue, index, val) {
        setFieldValue(`npiRoleMapping.${index}.newNpiRoleName`, val);
    }

    // Payment fee schedule handlers
    function handleTargetPaymentFeeScheduleChange(setFieldValue, groupIndex, ruleIndex, obj, groupType) {
        setFieldValue(`paymentFeeScheduleMapping.${groupType}.${groupIndex}.rules.${ruleIndex}.targetFeeScheduleId`, obj || { id: null, name: "" });
        // Clear the target label when fee schedule changes
        setFieldValue(`paymentFeeScheduleMapping.${groupType}.${groupIndex}.rules.${ruleIndex}.targetLabelId`, null);
    }

    function handleTargetPaymentLabelChange(setFieldValue, groupIndex, ruleIndex, obj, rule, groupType) {
        setFieldValue(`paymentFeeScheduleMapping.${groupType}.${groupIndex}.rules.${ruleIndex}.targetLabelId`, obj);
        validateLabelsFromTargetClient(targetClient?.schema, rule.targetFeeScheduleId.id, obj?.id, `${groupType}-${groupIndex}-${ruleIndex}`);
    }

    const isCopyNewName = (key) => copyModeToggles[key] !== false;

    const handleCopyModeToggle = (stepKey, index, checked, groupPath, group, setFieldValue) => {
        setCopyModeToggles(prev => ({ ...prev, [`${stepKey}-${index}`]: checked }));
        
        if (checked) {
             const nameToUse = group.newFeeScheduleName || group.feeScheduleName;

             if (!group.newFeeScheduleName) {
                 setFieldValue(`${groupPath}.${index}.newFeeScheduleName`, group.feeScheduleName);
             }
             
             group.rules?.forEach((rule, rIndex) => {
                 setFieldValue(`${groupPath}.${index}.rules.${rIndex}.targetFeeScheduleId`, { id: 0, name: '' });
                 setFieldValue(`${groupPath}.${index}.rules.${rIndex}.targetLabelId`, null);
             });

             // Trigger validation
             const typePart = groupPath.split('.').pop(); 
             let stepPart = stepKey.split('-')[0];
             if (stepKey.startsWith('step-lesserof')) {
                 stepPart = 'step-lesserof';
             }
             if (stepKey.startsWith('step-providerrole')) {
                stepPart = 'step-providerrole';
             }
             validateFeeScheduleNameExistence(nameToUse, targetClient?.schema, `${stepPart}-${typePart}`, index);
        } else {
             setFieldValue(`${groupPath}.${index}.newFeeScheduleName`, '');
             const typePart = groupPath.split('.').pop(); 
             let stepPart = stepKey.split('-')[0];
             if (stepKey.startsWith('step-lesserof')) {
                 stepPart = 'step-lesserof';
             }
             if (stepKey.startsWith('step-providerrole')) {
                stepPart = 'step-providerrole';
             }
             setNameExistErrors(prev => ({ ...prev, [`${stepPart}-${typePart}-${index}`]: null }));
             setNameValidatePending(prev => ({ ...prev, [`${stepPart}-${typePart}-${index}`]: false }));
        }
    };

    function handleNewPaymentFeeScheduleChange(setFieldValue, index, val, groupType) {
        setFieldValue(`paymentFeeScheduleMapping.${groupType}.${index}.newFeeScheduleName`, val);
        validateFeeScheduleNameExistence(val, targetClient?.schema, `step3-${groupType}`, index);
    }

    // Lesser of handlers
    function handleTargetLesserOfFeeScheduleChange(setFieldValue, groupIndex, ruleIndex, obj, groupType) {
        setFieldValue(`lesserOfMapping.${groupType}.${groupIndex}.rules.${ruleIndex}.targetFeeScheduleId`, obj || { id: null, name: "" });
        // Clear the target label when fee schedule changes
        setFieldValue(`lesserOfMapping.${groupType}.${groupIndex}.rules.${ruleIndex}.targetLabelId`, null);
    }

    function handleTargetLesserOfLabelChange(setFieldValue, groupIndex, ruleIndex, obj, rule, groupType) {
        setFieldValue(`lesserOfMapping.${groupType}.${groupIndex}.rules.${ruleIndex}.targetLabelId`, obj);
        validateLabelsFromTargetClient(targetClient?.schema, rule.targetFeeScheduleId.id, obj?.id, `lesserof-${groupType}-${groupIndex}-${ruleIndex}`);
    }

    function handleNewLesserOfFeeScheduleChange(setFieldValue, index, val, groupType) {
        setFieldValue(`lesserOfMapping.${groupType}.${index}.newFeeScheduleName`, val);
        validateFeeScheduleNameExistence(val, targetClient?.schema, `step-lesserof-${groupType}`, index);
    }

    // Provider Role handlers
    function handleTargetProviderRoleFeeScheduleChange(setFieldValue, groupIndex, ruleIndex, obj, groupType) {
        setFieldValue(`providerRoleMapping.${groupType}.${groupIndex}.rules.${ruleIndex}.targetFeeScheduleId`, obj || { id: null, name: "" });
        // Clear the target label when fee schedule changes
        setFieldValue(`providerRoleMapping.${groupType}.${groupIndex}.rules.${ruleIndex}.targetLabelId`, null);
    }

    function handleTargetProviderRoleLabelChange(setFieldValue, groupIndex, ruleIndex, obj, rule, groupType) {
        setFieldValue(`providerRoleMapping.${groupType}.${groupIndex}.rules.${ruleIndex}.targetLabelId`, obj);
        validateLabelsFromTargetClient(targetClient?.schema, rule.targetFeeScheduleId.id, obj?.id, `providerrole-${groupType}-${groupIndex}-${ruleIndex}`);
    }

    function handleNewProviderRoleFeeScheduleChange(setFieldValue, index, val, groupType) {
        setFieldValue(`providerRoleMapping.${groupType}.${index}.newFeeScheduleName`, val);
        validateFeeScheduleNameExistence(val, targetClient?.schema, `step-providerrole-${groupType}`, index);
    }

    function renderActionButtons(handleSubmit, values) {
        if (steps.length === 1) {
            return <>
                <Button
                    onClick={() => handleClosing(false)}
                    variant="contained"
                    type='button'
                    className={classes.button}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    className={`ml-2 ${classes.button}`}
                    type="button"
                    onClick={handleSubmit}
                    disabled={copyContractPending}
                >
                    {(copyContractPending) ? 'Saving..' : 'Save'}
                </Button>
            </>;
        } else if (activeStep + 1 === steps.length) {
            return <>
                <Button
                    onClick={handleBack}
                    variant="contained"
                    type='button'
                    className={classes.button}>
                    Back
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    className={`ml-2 ${classes.button}`}
                    type="button"
                    onClick={handleSubmit}
                    disabled={copyContractPending}
                >
                    {(copyContractPending) ? 'Saving..' : 'Save'}
                </Button>
            </>;
        } else if (activeStep === 0) {
            return <>
                <Button
                    onClick={() => handleClosing(false)}
                    variant="contained"
                    type='button'
                    className={classes.button}>
                    Cancel
                </Button>
                <Button
                    onClick={handleNext}
                    variant="contained"
                    color="primary"
                    type="button"
                    className={classes.button}>
                    Next
                </Button>
            </>;
        }
        else {
            return <>
                <Button
                    onClick={handleBack}
                    variant="contained"
                    type='button'
                    className={classes.button}>
                    Back
                </Button>
                <Button
                    onClick={() => handleNextWithValidation(values)}
                    variant="contained"
                    color="primary"
                    type="button"
                    className={classes.button}>
                    Next
                </Button>
            </>;
        }

    }

    function getFeeScheduleHelperText(touched, key, index, ruleIndex, type) {
        const mapping = type === 'localFeeSchedules' ? touched.feeScheduleMapping?.localFeeSchedules : touched.feeScheduleMapping?.globalFeeSchedules;
        if (mapping && mapping[index]) {
            if (ruleIndex !== undefined) {
                return mapping[index].rules?.[ruleIndex]?.[key];
            }
            return mapping[index][key];
        }
        return false;
    }

    function getFeeScheduleError(errors, key, index, ruleIndex, type) {
        // Check custom error map first
        const mapping = type === 'localFeeSchedules' ? errors.feeScheduleMapError?.localFeeSchedules : errors.feeScheduleMapError?.globalFeeSchedules;
        if (mapping && mapping[index]) {
            if (ruleIndex !== undefined) {
                if (mapping[index].rules?.[ruleIndex]?.[key]) return mapping[index].rules[ruleIndex][key];
            } else {
                if (mapping[index][key]) return mapping[index][key];
            }
        }
        return false;
    }

    function getCodeServiceHelperText(touched, key, index) {
        if (touched.codeServiceMapping) {
            if (touched.codeServiceMapping[index]) {
                return touched.codeServiceMapping[index][key];
            }
        }
        return false;
    }

    function getCodeServiceError(errors, key, index) {
        if (errors.codeServiceMapError) {
            if (errors.codeServiceMapError[index]) {
                return errors.codeServiceMapError[index][key];
            }
            return false;
        }
        return false;
    }

    function getNPIRoleHelperText(touched, key, index) {
        if (touched.npiRoleMapping) {
            if (touched.npiRoleMapping[index]) {
                return touched.npiRoleMapping[index][key];
            }
            return false;
        }
        return false;
    }

    function getNPIRoleError(errors, key, index) {
        if (errors.npiRoleMapError) {
            if (errors.npiRoleMapError[index]) {
                return errors.npiRoleMapError[index][key];
            }
            return false;
        }
        return false;
    }

    function getPaymentFeeScheduleHelperText(touched, key, index, ruleIndex, type) {
        const mapping = type === 'localFeeSchedules' ? touched.paymentFeeScheduleMapping?.localFeeSchedules : touched.paymentFeeScheduleMapping?.globalFeeSchedules;
        if (mapping && mapping[index]) {
            if (ruleIndex !== undefined) {
                return mapping[index].rules?.[ruleIndex]?.[key];
            }
            return mapping[index][key];
        }
        return false;
    }

    function getPaymentFeeScheduleError(errors, key, index, ruleIndex, type) {
        // Check custom error map first
        const mapping = type === 'localFeeSchedules' ? errors.paymentFeeScheduleMapError?.localFeeSchedules : errors.paymentFeeScheduleMapError?.globalFeeSchedules;
        if (mapping && mapping[index]) {
            if (ruleIndex !== undefined) {
                if (mapping[index].rules?.[ruleIndex]?.[key]) return mapping[index].rules[ruleIndex][key];
            } else {
                if (mapping[index][key]) return mapping[index][key];
            }
        }
        return false;
    }

    function getLesserOfHelperText(touched, key, index, ruleIndex, type) {
        const mapping = type === 'localFeeSchedules' ? touched.lesserOfMapping?.localFeeSchedules : touched.lesserOfMapping?.globalFeeSchedules;
        if (mapping && mapping[index]) {
            if (ruleIndex !== undefined) {
                return mapping[index].rules?.[ruleIndex]?.[key];
            }
            return mapping[index][key];
        }
        return false;
    }

    function getLesserOfError(errors, key, index, ruleIndex, type) {
        const mapping = type === 'localFeeSchedules' ? errors.lesserOfMapError?.localFeeSchedules : errors.lesserOfMapError?.globalFeeSchedules;
        if (mapping && mapping[index]) {
            if (ruleIndex !== undefined) {
                return mapping[index].rules?.[ruleIndex]?.[key];
            }
            return mapping[index][key];
        }
        return false;
    }

    function getProviderRoleHelperText(touched, key, index, ruleIndex, type) {
        const mapping = type === 'localFeeSchedules' ? touched.providerRoleMapping?.localFeeSchedules : touched.providerRoleMapping?.globalFeeSchedules;
        if (mapping && mapping[index]) {
            if (ruleIndex !== undefined) {
                return mapping[index].rules?.[ruleIndex]?.[key];
            }
            return mapping[index][key];
        }
        return false;
    }

    function getProviderRoleError(errors, key, index, ruleIndex, type) {
        const mapping = type === 'localFeeSchedules' ? errors.providerRoleMapError?.localFeeSchedules : errors.providerRoleMapError?.globalFeeSchedules;
        if (mapping && mapping[index]) {
            if (ruleIndex !== undefined) {
                return mapping[index].rules?.[ruleIndex]?.[key];
            }
            return mapping[index][key];
        }
        return false;
    }

    const feeScheduleLabelsCallback = (data,id) => {
        setFeeScheduleLabels({...feeScheduleLabels,[id]:data})        
        
    }
    const fetchItemsBasedOnTargetClient = (rule) => {
        if(rule?.targetFeeScheduleId?.id in feeScheduleLabels===false)
        { 
            getFeeScheduleLabelsFromTargetClient(targetClient?.schema,rule.targetFeeScheduleId.id,feeScheduleLabelsCallback)
        }        
        
    }
    // const name = (params) => {
        
    // }

    const filterOptions = createFilterOptions<any>({
        stringify: option => option.name
    });


    return (
        <Dialog maxWidth={steps.length > 1 ? 'lg' : 'md'} fullWidth open={open} aria-labelledby="form-dialog-title" sx={{'& .MuiDialog-paper':{maxHeight:'inherit !important'}}}>
            <DialogTitle id="copy-contract-title" sx={{ pb: steps.length > 1 ? 1 : 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Copy Contract</Typography>
                    <IconButton
                        aria-label="close"
                        onClick={() => handleClosing(false)}
                        sx={{
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
                {steps.length > 1 && (
                    <Box sx={{ mt: 2, mb: 1 }}>
                        <Stepper activeStep={activeStep} alternativeLabel>
                            {steps.map(({ label }) => (
                                <Step key={label}>
                                    <StepLabel><h4 className='font-16'>{label}</h4></StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </Box>
                )}
            </DialogTitle>
            <Loadable loading={copyContractPending || getFeeSchdulePending || getRulesPending || getCodeServicePending || facilitiesFromTargetClientPending}>
                <Formik
                    enableReinitialize={true}
                    initialValues={initialValues}
                    innerRef={formikRef}
                    validate={values => {
                        const errors : any = {};
                        
                        // Step 1: Details
                        if (steps[activeStep]?.label === copyContractConstants.DETAILS) {
                            if (!values.newContractName) {
                                errors.newContractName = intl.formatMessage({
                                    id: "AUTH.VALIDATION.REQUIRED_FIELD"
                                });
                            }
                        }

                        // Step 2: Fee Schedule Mapping
                        if (steps[activeStep]?.label === copyContractConstants.FEESCHEDULE) {
                            const validateFSList = (list, allItems, isGlobal, toggleKeyPrefix) => {
                                 return list?.map((group, groupIndex) => {
                                    const groupErrors = {};
                                    const isToggleOn = !isGlobal && toggleKeyPrefix ? isCopyNewName(`${toggleKeyPrefix}-${groupIndex}`) : false;

                                    const rulesErrors = group.rules?.map((rule, ruleIndex) => {
                                        const ruleErrorObj = {};
                                        let hasRuleError = false;

                                        if (rule.isRBRVS || rule?.isDME) {
                                            if (!rule.selectedState) {
                                                ruleErrorObj['selectedState'] = intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" });
                                                hasRuleError = true;
                                            }
                                            if (!rule.selectedGpci && rule.isRBRVS) {
                                                ruleErrorObj['selectedGpci'] = intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" });
                                                hasRuleError = true;
                                            }
                                            if (!rule.selectedLocality && rule.isRBRVS) {
                                                ruleErrorObj['selectedLocality'] = intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" });
                                                hasRuleError = true;
                                            }
                                        }

                                        if (!isGlobal && !isToggleOn) {
                                            if (!rule.targetFeeScheduleId?.id || rule.targetFeeScheduleId.id === 0) {
                                                ruleErrorObj['targetFeeScheduleId'] = intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" });
                                                hasRuleError = true;
                                            }
                                        }

                                        return hasRuleError ? ruleErrorObj : {};
                                    }) || [];

                                    if (!isGlobal && isToggleOn) {
                                        if (!group.newFeeScheduleName) {
                                            groupErrors['newFeeScheduleName'] = intl.formatMessage({
                                                id: "AUTH.VALIDATION.REQUIRED_FIELD"
                                            });
                                        }
                                    } else if (isGlobal) {
                                        const isNewFSNameRequired = group.rules?.some(r => !r.targetFeeScheduleId?.id || r.targetFeeScheduleId.id === 0);
                                        if (isNewFSNameRequired && !group.newFeeScheduleName) {
                                            groupErrors['newFeeScheduleName'] = intl.formatMessage({
                                                id: "AUTH.VALIDATION.REQUIRED_FIELD"
                                            });
                                        }
                                    }

                                    if (group.newFeeScheduleName) {
                                        if (group.newFeeScheduleName.length <= 3) {
                                            groupErrors['newFeeScheduleName'] = "Fee Schedule Name must be greater than 3 characters";
                                        } else {
                                            const isFSDuplicate = allItems.some((otherGroup) => 
                                                otherGroup !== group && otherGroup.newFeeScheduleName === group.newFeeScheduleName
                                            );
                                        
                                            if (isFSDuplicate) {
                                                groupErrors['newFeeScheduleName'] = "Duplicate Fee Schedule Name";
                                            }
                                        }
                                    }

                                    const hasRulesErrors = rulesErrors.some(r => Object.keys(r).length > 0);
                                    if (Object.keys(groupErrors).length > 0 || hasRulesErrors) {
                                        return { 
                                            ...groupErrors,
                                            rules: rulesErrors 
                                        };
                                    }
                                    return {}; 
                                 }) || [];
                            };

                            const allFSItems = [...(values?.feeScheduleMapping?.localFeeSchedules||[]), ...(values?.feeScheduleMapping?.globalFeeSchedules||[])];
                            const feeScheduleMapError = {
                                localFeeSchedules: validateFSList(values?.feeScheduleMapping?.localFeeSchedules, allFSItems, false, 'step2-local'),
                                globalFeeSchedules: validateFSList(values?.feeScheduleMapping?.globalFeeSchedules, allFSItems, true, undefined)
                            };

                            const hasLocalErrors = feeScheduleMapError.localFeeSchedules.some(value => Object.keys(value).length > 0);
                            const hasGlobalErrors = feeScheduleMapError.globalFeeSchedules.some(value => Object.keys(value).length > 0);

                            if (hasLocalErrors || hasGlobalErrors) {
                                errors['feeScheduleMapError'] = feeScheduleMapError;
                            }
                        }

                        // Step 3: Payment Fee Schedule Mapping
                        if (steps[activeStep]?.label === copyContractConstants.PAYMENTFEESCHEDULE) {
                            const validatePaymentList = (list, isGlobal = false, toggleKeyPrefix) => {
                                const errorList = list?.map((group, index) => {
                                    const groupErrorObj = {};
                                    let groupHasError = false;
                                    const isToggleOn = !isGlobal && toggleKeyPrefix ? isCopyNewName(`${toggleKeyPrefix}-${index}`) : false;

                                    if (!isGlobal && isToggleOn) {
                                        if (!group.newFeeScheduleName) {
                                            groupErrorObj['newFeeScheduleName'] = intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" });
                                            groupHasError = true;
                                        }
                                    } else if (isGlobal) {
                                        const isNewFSNameRequired = group.rules?.some(r => !r.targetFeeScheduleId?.id || r.targetFeeScheduleId.id === 0);
                                        if (isNewFSNameRequired && !group.newFeeScheduleName) {
                                            groupErrorObj['newFeeScheduleName'] = intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" });
                                            groupHasError = true;
                                        }
                                    }

                                    if (group.newFeeScheduleName) {
                                        if (group.newFeeScheduleName.length <= 3) {
                                            groupErrorObj['newFeeScheduleName'] = "Fee Schedule Name must be greater than 3 characters";
                                            groupHasError = true;
                                        } else {
                                            const isDuplicate = list.some((otherGroup, otherIndex) => 
                                                otherIndex !== index && otherGroup.newFeeScheduleName === group.newFeeScheduleName
                                            );
                                            if (isDuplicate) {
                                                groupErrorObj['newFeeScheduleName'] = "Duplicate Fee Schedule Name";
                                                groupHasError = true;
                                            }
                                        }
                                    }

                                    const rulesErrors = group.rules?.map((rule, ruleIndex) => {
                                        const ruleErrorObj = {};
                                        let ruleHasError = false;

                                        if (rule.isRBRVS || rule?.isDME) {
                                            if (!rule.selectedState) {
                                                ruleErrorObj['selectedState'] = intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" });
                                                ruleHasError = true;
                                            }
                                            if (!rule.selectedGpci && rule.isRBRVS) {
                                                ruleErrorObj['selectedGpci'] = intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" });
                                                ruleHasError = true;
                                            }
                                            if (!rule.selectedLocality && rule.isRBRVS) {
                                                ruleErrorObj['selectedLocality'] = intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" });
                                                ruleHasError = true;
                                            }
                                        }

                                        if (!isGlobal && !isToggleOn) {
                                            if (!rule.targetFeeScheduleId?.id || rule.targetFeeScheduleId.id === 0) {
                                                ruleErrorObj['targetFeeScheduleId'] = intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" });
                                                ruleHasError = true;
                                            }
                                        }

                                        if (rule.targetFeeScheduleId?.id && rule.targetFeeScheduleId.id !== 0 && !rule.targetLabelId) {
                                            ruleErrorObj['targetLabelId'] = intl.formatMessage({
                                                id: "AUTH.VALIDATION.REQUIRED_FIELD"
                                            });
                                            ruleHasError = true;
                                        }

                                        if (ruleHasError) return ruleErrorObj;
                                        return {};
                                    });

                                    if (rulesErrors?.some(e => Object.keys(e).length > 0)) {
                                        groupErrorObj['rules'] = rulesErrors;
                                        groupHasError = true;
                                    }

                                    if (groupHasError) return groupErrorObj;
                                    return {};
                                });

                                return errorList;
                            };

                            const localPaymentErrors = validatePaymentList(values.paymentFeeScheduleMapping?.localFeeSchedules, false, 'step3-local');
                            const globalPaymentErrors = validatePaymentList(values.paymentFeeScheduleMapping?.globalFeeSchedules, true, undefined);

                            if (localPaymentErrors?.some(e => Object.keys(e).length > 0) || globalPaymentErrors?.some(e => Object.keys(e).length > 0)) {
                                errors['paymentFeeScheduleMapError'] = {
                                    localFeeSchedules: localPaymentErrors,
                                    globalFeeSchedules: globalPaymentErrors
                                };
                            }
                        }

                        // Step 4: Lesser of Configuration Mapping
                        if (steps[activeStep]?.label === copyContractConstants.LESSEROF) {
                            const validateLesserOfList = (list, isGlobal = false, togglePrefix = '') => {
                                const errorList = list?.map((group, index) => {
                                    const groupErrorObj = {};
                                    let groupHasError = false;
                                    const isToggleOn = !isGlobal && togglePrefix ? isCopyNewName(`${togglePrefix}-${index}`) : false;

                                    if (!isGlobal && isToggleOn) {
                                        if (!group.newFeeScheduleName) {
                                            groupErrorObj['newFeeScheduleName'] = intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" });
                                            groupHasError = true;
                                        }
                                    } else if (isGlobal) {
                                        const isNewFSNameRequired = group.rules?.some(r => !r.targetFeeScheduleId?.id || r.targetFeeScheduleId.id === 0);
                                        if (isNewFSNameRequired && !group.newFeeScheduleName) {
                                            groupErrorObj['newFeeScheduleName'] = intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" });
                                            groupHasError = true;
                                        }
                                    }

                                    if (group.newFeeScheduleName) {
                                        if (group.newFeeScheduleName.length <= 3) {
                                            groupErrorObj['newFeeScheduleName'] = "Fee Schedule Name must be greater than 3 characters";
                                            groupHasError = true;
                                        } else {
                                            const isDuplicate = list.some((otherGroup, otherIndex) =>
                                                otherIndex !== index && otherGroup.newFeeScheduleName === group.newFeeScheduleName
                                            );
                                            if (isDuplicate) {
                                                groupErrorObj['newFeeScheduleName'] = "Duplicate Fee Schedule Name";
                                                groupHasError = true;
                                            }
                                        }
                                    }

                                    const rulesErrors = group.rules?.map((rule, ruleIndex) => {
                                        const ruleErrorObj = {};
                                        let ruleHasError = false;

                                        if (!isGlobal && !isToggleOn) {
                                            if (!rule.targetFeeScheduleId?.id || rule.targetFeeScheduleId.id === 0) {
                                                ruleErrorObj['targetFeeScheduleId'] = intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" });
                                                ruleHasError = true;
                                            }
                                        }

                                        if (rule.targetFeeScheduleId?.id && rule.targetFeeScheduleId.id !== 0 && !rule.targetLabelId) {
                                            ruleErrorObj['targetLabelId'] = intl.formatMessage({
                                                id: "AUTH.VALIDATION.REQUIRED_FIELD"
                                            });
                                            ruleHasError = true;
                                        }

                                        if (ruleHasError) return ruleErrorObj;
                                        return false;
                                    });

                                    // RBRVS validation
                                    group.rules?.forEach((rule, ruleIndex) => {
                                        if (rule.isRBRVS || rule?.isDME) {
                                            let rbrvsHasError = false;
                                            const ruleError = (rulesErrors[ruleIndex] as any) || {};

                                            if (!rule.selectedState) {
                                                ruleError['selectedState'] = intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" });
                                                rbrvsHasError = true;
                                            }
                                            if (!rule.selectedGpci && rule.isRBRVS) {
                                                ruleError['selectedGpci'] = intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" });
                                                rbrvsHasError = true;
                                            }
                                            if (!rule.selectedLocality && rule.isRBRVS) {
                                                ruleError['selectedLocality'] = intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" });
                                                rbrvsHasError = true;
                                            }

                                            if (rbrvsHasError) {
                                                rulesErrors[ruleIndex] = ruleError;
                                            }
                                        }
                                    });

                                    if (rulesErrors?.some(e => e !== false)) {
                                        groupErrorObj['rules'] = rulesErrors;
                                        groupHasError = true;
                                    }

                                    if (groupHasError) return groupErrorObj;
                                    return false;
                                });

                                return errorList;
                            };

                            const localLesserOfErrors = validateLesserOfList(values.lesserOfMapping?.localFeeSchedules, false, 'step-lesserof-local');
                            const globalLesserOfErrors = validateLesserOfList(values.lesserOfMapping?.globalFeeSchedules, true, undefined);

                            if (localLesserOfErrors?.some(e => e !== false) || globalLesserOfErrors?.some(e => e !== false)) {
                                errors['lesserOfMapError'] = {
                                    localFeeSchedules: localLesserOfErrors,
                                    globalFeeSchedules: globalLesserOfErrors
                                };
                            }
                        }

                        // Step 5 (or 6): Code Service Mapping
                        if (steps[activeStep]?.label === copyContractConstants.CODESERVICE) {
                            const codeServiceMapError = values.codeServiceMapping?.map((rule, index, array) => {
                                const codeServiceMapErrorObj = {};
                                let hasError = false;

                                if (rule.targetCodeServiceId.id === 0 && !rule.newCodeServiceName) {
                                    codeServiceMapErrorObj['newCodeServiceName'] = intl.formatMessage({
                                        id: "AUTH.VALIDATION.REQUIRED_FIELD"
                                    });
                                    hasError = true;
                                }

                                if (rule.newCodeServiceName) {
                                    const isCodeServiceDuplicate = array.some((otherRule, otherIndex) => 
                                        otherIndex !== index && otherRule.newCodeServiceName === rule.newCodeServiceName
                                    );
                                
                                    if (isCodeServiceDuplicate) {
                                        codeServiceMapErrorObj['newCodeServiceName'] = "Duplicate Code Service Name";
                                        hasError = true;
                                    }
                                }

                                if (hasError) {
                                    return codeServiceMapErrorObj;
                                }

                                return false;
                            });

                            const filteredCodeArray = codeServiceMapError?.filter(value => Object.keys(value).length > 0);

                            if (filteredCodeArray?.length > 0) {
                                errors['codeServiceMapError'] = codeServiceMapError;
                            }
                        }

                        // Step 6 : Provider Role Mapping
                        if (steps[activeStep]?.label === copyContractConstants.NPIROLE) {
                            const npiRoleMapError = values.npiRoleMapping?.map((rule, index, array) => {
                                const npiRoleMapErrorObj = {};
                                let hasError = false;

                                if (rule.targetNpiRoleId.id === 0 && !rule.newNpiRoleName) {
                                    npiRoleMapErrorObj['newNpiRoleName'] = intl.formatMessage({
                                        id: "AUTH.VALIDATION.REQUIRED_FIELD"
                                    });
                                    hasError = true;
                                }

                                if (rule.newNpiRoleName) {
                                    const isNPIRoleDuplicate = array.some((otherRule, otherIndex) => 
                                        otherIndex !== index && otherRule.newNpiRoleName === rule.newNpiRoleName
                                    );
                                
                                    if (isNPIRoleDuplicate) {
                                        npiRoleMapErrorObj['newNpiRoleName'] = "Duplicate Provider Role Name";
                                        hasError = true;
                                    }
                                }

                                if (hasError) {
                                    return npiRoleMapErrorObj;
                                }

                                return false;
                            });

                            const filteredNPIRoleArray = npiRoleMapError?.filter(value => Object.keys(value).length > 0) || [];

                            if (filteredNPIRoleArray.length > 0) {
                                errors['npiRoleMapError'] = npiRoleMapError;
                            }
                        }

                        // Step 7: Provider Role Mapping
                        if (steps[activeStep]?.label === copyContractConstants.PROVIDERROLE) {
                            const validateProviderRoleList = (list, isGlobal = false, togglePrefix = '') => {
                                const errorList = list?.map((group, index) => {
                                    const groupErrorObj = {};
                                    let groupHasError = false;
                                    const isToggleOn = !isGlobal && togglePrefix ? isCopyNewName(`${togglePrefix}-${index}`) : false;

                                    if (!isGlobal && isToggleOn) {
                                        if (!group.newFeeScheduleName) {
                                            groupErrorObj['newFeeScheduleName'] = intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" });
                                            groupHasError = true;
                                        }
                                    } else if (isGlobal) {
                                        const isNewFSNameRequired = group.rules?.some(r => !r.targetFeeScheduleId?.id || r.targetFeeScheduleId.id === 0);
                                        if (isNewFSNameRequired && !group.newFeeScheduleName) {
                                            groupErrorObj['newFeeScheduleName'] = intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" });
                                            groupHasError = true;
                                        }
                                    }

                                    if (group.newFeeScheduleName) {
                                        if (group.newFeeScheduleName.length <= 3) {
                                            groupErrorObj['newFeeScheduleName'] = "Fee Schedule Name must be greater than 3 characters";
                                            groupHasError = true;
                                        } else {
                                            const isDuplicate = list.some((otherGroup, otherIndex) =>
                                                otherIndex !== index && otherGroup.newFeeScheduleName === group.newFeeScheduleName
                                            );
                                            if (isDuplicate) {
                                                groupErrorObj['newFeeScheduleName'] = "Duplicate Fee Schedule Name";
                                                groupHasError = true;
                                            }
                                        }
                                    }

                                    const rulesErrors = group.rules?.map((rule, ruleIndex) => {
                                        const ruleErrorObj = {};
                                        let ruleHasError = false;

                                        if (!isGlobal && !isToggleOn) {
                                            if (!rule.targetFeeScheduleId?.id || rule.targetFeeScheduleId.id === 0) {
                                                ruleErrorObj['targetFeeScheduleId'] = intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" });
                                                ruleHasError = true;
                                            }
                                        }

                                        if (rule.targetFeeScheduleId?.id && rule.targetFeeScheduleId.id !== 0 && !rule.targetLabelId) {
                                            ruleErrorObj['targetLabelId'] = intl.formatMessage({
                                                id: "AUTH.VALIDATION.REQUIRED_FIELD"
                                            });
                                            ruleHasError = true;
                                        }

                                        if (ruleHasError) return ruleErrorObj;
                                        return false;
                                    });

                                    // RBRVS validation
                                    group.rules?.forEach((rule, ruleIndex) => {
                                        if (rule.isRBRVS || rule?.isDME) {
                                            let rbrvsHasError = false;
                                            const ruleError = (rulesErrors[ruleIndex] as any) || {};

                                            if (!rule.selectedState) {
                                                ruleError['selectedState'] = intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" });
                                                rbrvsHasError = true;
                                            }
                                            if (!rule.selectedGpci && rule.isRBRVS) {
                                                ruleError['selectedGpci'] = intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" });
                                                rbrvsHasError = true;
                                            }
                                            if (!rule.selectedLocality && rule.isRBRVS) {
                                                ruleError['selectedLocality'] = intl.formatMessage({ id: "AUTH.VALIDATION.REQUIRED_FIELD" });
                                                rbrvsHasError = true;
                                            }

                                            if (rbrvsHasError) {
                                                rulesErrors[ruleIndex] = ruleError;
                                            }
                                        }
                                    });

                                    if (rulesErrors?.some(e => e !== false)) {
                                        groupErrorObj['rules'] = rulesErrors;
                                        groupHasError = true;
                                    }

                                    if (groupHasError) return groupErrorObj;
                                    return false;
                                });

                                return errorList;
                            };

                            const localProviderRoleErrors = validateProviderRoleList(values.providerRoleMapping?.localFeeSchedules, false, 'step-providerrole-local');
                            const globalProviderRoleErrors = validateProviderRoleList(values.providerRoleMapping?.globalFeeSchedules, true, undefined);

                            if (localProviderRoleErrors?.some(e => e !== false) || globalProviderRoleErrors?.some(e => e !== false)) {
                                errors['providerRoleMapError'] = {
                                    localFeeSchedules: localProviderRoleErrors,
                                    globalFeeSchedules: globalProviderRoleErrors
                                };
                            }
                        }
                        
                        return errors;
                    }}
                    onSubmit={(values) => {

                        let availibilityError=false
                        const checkAvailability = (list, isGlobal = false, togglePrefix = '', groupType = '') => {
                            list?.forEach((group, groupIndex) => {
                                const isToggleOn = !isGlobal ? isCopyNewName(`${togglePrefix}-${groupIndex}`) : false;
                                
                                if (!isToggleOn) {
                                    group.rules?.forEach((rule, ruleIndex) => {
                                        if(rule?.targetFeeScheduleId?.id !== 0 && feeScheduleLabelsValidate[`done-${groupType}-${groupIndex}-${ruleIndex}`] === true) {
                                            availibilityError = true;
                                        }
                                    });
                                }
                            });

                        };
                        checkAvailability(values.paymentFeeScheduleMapping?.localFeeSchedules, false, 'step3-local', 'localFeeSchedules');
                        checkAvailability(values.paymentFeeScheduleMapping?.globalFeeSchedules, true, '', 'globalFeeSchedules');

                        checkAvailability(values.lesserOfMapping?.localFeeSchedules, false, 'step-lesserof-local', 'lesserof-localFeeSchedules');
                        checkAvailability(values.lesserOfMapping?.globalFeeSchedules, true, '', 'lesserof-globalFeeSchedules');

                        checkAvailability(values.providerRoleMapping?.localFeeSchedules, false, 'step-providerrole-local', 'providerrole-localFeeSchedules');
                        checkAvailability(values.providerRoleMapping?.globalFeeSchedules, true, '', 'providerrole-globalFeeSchedules');
                        if(availibilityError)
                        {
                            return
                        }
                        if (isSuperAdmin && targetClient?.clientId !== selectedClient) {
                            let payload = {
                                sourceClient: selectedClientSchema,
                                targetClient: targetClient?.schema,
                                targetClientId: targetClient?.clientId,
                                contractId: contractId,
                                newContractStateId: stateId,
                                newContractName: values.newContractName,
                                targetFacilities: values.targetFacilities?.map(item => item.id),
                                feeScheduleMapping: [
                                    ...(values.feeScheduleMapping?.localFeeSchedules || []).flatMap(group => 
                                        (group.rules || []).map(rule => ({
                                            ruleId: rule.ruleId,
                                            sourceFeeScheduleId: group.sourceFeeScheduleId || 0,
                                            targetFeeScheduleId: rule.targetFeeScheduleId?.id || 0,
                                            newFeeScheduleName: group.newFeeScheduleName,
                                            targetFeeScheduleLabelXrefId: rule.targetFeeScheduleLabelXrefId || 0,
                                            sourceFeeScheduleLabelXrefId: null,
                                            sourceGlobalFeeScheduleId: 0,
                                            newGlobalFeeScheduleState: rule.selectedState || null,
                                            newGlobalFeeScheduleGPCI: rule.selectedGpci || null,
                                            newGlobalFeeScheduleLocality: rule.selectedLocality || null
                                        }))
                                    ),
                                    ...(values.feeScheduleMapping?.globalFeeSchedules || []).flatMap(group => 
                                        (group.rules || []).map(rule => ({
                                            ruleId: rule.ruleId,
                                            sourceFeeScheduleId: 0,
                                            targetFeeScheduleId: 0,
                                            newFeeScheduleName: group.feeScheduleName,
                                            targetFeeScheduleLabelXrefId: rule.targetFeeScheduleLabelXrefId || 0,
                                            sourceFeeScheduleLabelXrefId: null,
                                            sourceGlobalFeeScheduleId: group.sourceFeeScheduleId || 0,
                                            newGlobalFeeScheduleState: rule.selectedState || null,
                                            newGlobalFeeScheduleGPCI: rule.selectedGpci || null,
                                            newGlobalFeeScheduleLocality: rule.selectedLocality || null
                                        }))
                                    )
                                ],
                                ruleFeeScheduleGroupXrefMapping: [
                                    ...(values.paymentFeeScheduleMapping?.localFeeSchedules || []).flatMap((group, groupIndex) => {
                                        const isToggleOn = isCopyNewName(`step3-local-${groupIndex}`);
                                        return (group.rules || []).map(rule => ({
                                            ruleId: rule.ruleId,
                                            ruleFeeScheduleGroupXrefId: rule.ruleFeeScheduleGroupXrefId,
                                            targetFeeScheduleId: !isToggleOn ? (rule.targetFeeScheduleId?.id || 0) : 0,
                                            targetLabelId: !isToggleOn ? (rule.targetLabelId?.id || null) : null,
                                            newFeeScheduleName: isToggleOn ? group.newFeeScheduleName : "",
                                            newGlobalFeeScheduleState: rule.selectedState || null,
                                            newGlobalFeeScheduleGPCI: rule.selectedGpci || null,
                                            newGlobalFeeScheduleLocality: rule.selectedLocality || null
                                        }))
                                    }),
                                    ...(values.paymentFeeScheduleMapping?.globalFeeSchedules || []).flatMap(group => 
                                        (group.rules || []).map(rule => ({
                                            ruleId: rule.ruleId,
                                            ruleFeeScheduleGroupXrefId: rule.ruleFeeScheduleGroupXrefId,
                                            targetFeeScheduleId: rule.targetFeeScheduleId?.id || 0,
                                            targetLabelId: rule.targetLabelId?.id || null,
                                            newFeeScheduleName: group.feeScheduleName,
                                            newGlobalFeeScheduleState: rule.selectedState || null,
                                            newGlobalFeeScheduleGPCI: rule.selectedGpci || null,
                                            newGlobalFeeScheduleLocality: rule.selectedLocality || null
                                        }))
                                    )
                                ],
                                lesserOfFeeScheduleMapping: [
                                    ...(values.lesserOfMapping?.localFeeSchedules || []).flatMap((group, groupIndex) => {
                                        const isToggleOn = isCopyNewName(`step-lesserof-local-${groupIndex}`);
                                        return (group.rules || []).map(rule => ({
                                            ruleId: rule.ruleId,
                                            configurationId: rule.lesserOfConfigurationId,
                                            targetFeeScheduleId: !isToggleOn ? (rule.targetFeeScheduleId?.id || 0) : 0,
                                            targetLabelId: !isToggleOn ? (rule.targetLabelId?.id || null) : null,
                                            newFeeScheduleName: isToggleOn ? group.newFeeScheduleName : "",
                                            newGlobalFeeScheduleState: rule.selectedState || null,
                                            newGlobalFeeScheduleGPCI: rule.selectedGpci || null,
                                            newGlobalFeeScheduleLocality: rule.selectedLocality || null
                                        }))
                                    }),
                                    ...(values.lesserOfMapping?.globalFeeSchedules || []).flatMap(group =>
                                        (group.rules || []).map(rule => ({
                                            ruleId: rule.ruleId,
                                            configurationId: rule.lesserOfConfigurationId,
                                            targetFeeScheduleId: rule.targetFeeScheduleId?.id || 0,
                                            targetLabelId: rule.targetLabelId?.id || null,
                                            newFeeScheduleName: group.feeScheduleName,
                                            newGlobalFeeScheduleState: rule.selectedState || null,
                                            newGlobalFeeScheduleGPCI: rule.selectedGpci || null,
                                            newGlobalFeeScheduleLocality: rule.selectedLocality || null
                                        }))
                                    )
                                ],
                                codeServiceMapping: values.codeServiceMapping.map(item => ({ ...item, targetCodeServiceId: item.targetCodeServiceId?.id })),
                                npiRoleMapping: values.npiRoleMapping?.map(item => ({ ...item, targetNpiRoleId: item.targetNpiRoleId?.id, newNpiRoleName: item.targetNpiRoleId?.id !== 0 ? "" : item.newNpiRoleName })),
                                providerRoleGroupFsMapping: [
                                    ...(values.providerRoleMapping?.localFeeSchedules || []).flatMap((group, groupIndex) => {
                                        const isToggleOn = isCopyNewName(`step-providerrole-local-${groupIndex}`);
                                        return (group.rules || []).map(rule => ({
                                            ruleId: rule.ruleId,
                                            providerRolePaymentGroupXrefId: rule.providerRolePaymentGroupXrefId,
                                            feeScheduleId: group.sourceFeeScheduleId || null,
                                            targetFeeScheduleId: !isToggleOn ? (rule.targetFeeScheduleId?.id || 0) : 0,
                                            targetLabelId: !isToggleOn ? (rule.targetLabelId?.id || null) : null,
                                            newFeeScheduleName: isToggleOn ? group.newFeeScheduleName : "",
                                            newGlobalFeeScheduleState: rule.selectedState || null,
                                            newGlobalFeeScheduleGPCI: rule.selectedGpci || null,
                                            newGlobalFeeScheduleLocality: rule.selectedLocality || null
                                        }))
                                    }),
                                    ...(values.providerRoleMapping?.globalFeeSchedules || []).flatMap(group =>
                                        (group.rules || []).map(rule => ({
                                            ruleId: rule.ruleId,
                                            providerRolePaymentGroupXrefId: rule.providerRolePaymentGroupXrefId,
                                            globalFeeScheduleId: group.sourceFeeScheduleId || null,
                                            targetFeeScheduleId: rule.targetFeeScheduleId?.id || 0,
                                            targetLabelId: rule.targetLabelId?.id || null,
                                            newFeeScheduleName: group.feeScheduleName,
                                            newGlobalFeeScheduleState: rule.selectedState || null,
                                            newGlobalFeeScheduleGPCI: rule.selectedGpci || null,
                                            newGlobalFeeScheduleLocality: rule.selectedLocality || null
                                        }))
                                    )
                                ],
                            }

                            copyContract(payload, handleCallback)  
                             //this calls correct API based on payload
                        }
                        else {
                            let payload = {
                                contractId: contractId,
                                newContractName: values.newContractName,
                                newContractStateId: stateId,
                            }
                            copyContract(payload, handleCallback)
                        }
                    }}
                >
                    {({
                        values,
                        errors,
                        touched,
                        handleChange,
                        handleBlur,
                        handleSubmit,
                        setFieldValue,
                        submitCount,
                    }) => (
                        <form onSubmit={handleSubmit} noValidate autoComplete="off">
                            <DialogContent dividers sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
                                <div className={classes.root}>
                                        {activeStep === 0 ? 
                                            <BasicDetailsStep
                                                contractName={contractName}
                                                values={values}
                                                touched={touched}
                                                errors={errors}
                                                handleBlur={handleBlur}
                                                handleChange={handleChange}
                                                setCopyContractName={setCopyContractName}
                                                targetClient={targetClient}
                                                setTargetClient={setTargetClient}
                                                handleGetRulesForCrossClient={handleGetRulesForCrossClient}
                                                clients={clients}
                                                isSuperAdmin={isSuperAdmin}
                                                selectedClient={selectedClient}
                                                setFieldValue={setFieldValue}
                                                facilitiesFromTargetClient={facilitiesFromTargetClient}
                                                classes={classes}
                                            />
                                         : steps[activeStep]?.label === copyContractConstants.FEESCHEDULE ? 
                                            <FeeScheduleMappingStep
                                                values={values}
                                                touched={touched}
                                                errors={errors}
                                                classes={classes}
                                                isCopyNewName={isCopyNewName}
                                                handleCopyModeToggle={handleCopyModeToggle}
                                                setFieldValue={setFieldValue}
                                                targetClient={targetClient}
                                                handleTargetFeeScheduleChange={handleTargetFeeScheduleChange}
                                                handleNewFeeScheduleChange={handleNewFeeScheduleChange}
                                                handleBlur={handleBlur}
                                                getFeeScheduleHelperText={getFeeScheduleHelperText}
                                                getFeeScheduleError={getFeeScheduleError}
                                                nameExistErrors={nameExistErrors}
                                                nameValidatePending={nameValidatePending}
                                                gpciLocalityData={gpciLocalityData}
                                            />
                                         : steps[activeStep]?.label === copyContractConstants.PAYMENTFEESCHEDULE ? 
                                            <PaymentFeeScheduleMappingStep
                                                values={values}
                                                touched={touched}
                                                errors={errors}
                                                classes={classes}
                                                isCopyNewName={isCopyNewName}
                                                handleCopyModeToggle={handleCopyModeToggle}
                                                setFieldValue={setFieldValue}
                                                targetClient={targetClient}
                                                handleTargetPaymentFeeScheduleChange={handleTargetPaymentFeeScheduleChange}
                                                handleTargetPaymentLabelChange={handleTargetPaymentLabelChange}
                                                handleNewPaymentFeeScheduleChange={handleNewPaymentFeeScheduleChange}
                                                handleBlur={handleBlur}
                                                getPaymentFeeScheduleHelperText={getPaymentFeeScheduleHelperText}
                                                getPaymentFeeScheduleError={getPaymentFeeScheduleError}
                                                nameExistErrors={nameExistErrors}
                                                nameValidatePending={nameValidatePending}
                                                gpciLocalityData={gpciLocalityData}
                                                feeScheduleLabels={feeScheduleLabels}
                                                fetchItemsBasedOnTargetClient={fetchItemsBasedOnTargetClient}
                                                feeScheduleLabelsValidate={feeScheduleLabelsValidate}
                                                getFeeScheduleLabelsFromTargetClientsPending={getFeeScheduleLabelsFromTargetClientsPending}
                                            />
                                         : steps[activeStep]?.label === copyContractConstants.LESSEROF ?
                                            <LesserOfMappingStep
                                                values={values}
                                                touched={touched}
                                                errors={errors}
                                                classes={classes}
                                                isCopyNewName={isCopyNewName}
                                                handleCopyModeToggle={handleCopyModeToggle}
                                                setFieldValue={setFieldValue}
                                                targetClient={targetClient}
                                                handleTargetLesserOfFeeScheduleChange={handleTargetLesserOfFeeScheduleChange}
                                                handleTargetLesserOfLabelChange={handleTargetLesserOfLabelChange}
                                                handleNewLesserOfFeeScheduleChange={handleNewLesserOfFeeScheduleChange}
                                                handleBlur={handleBlur}
                                                getLesserOfHelperText={getLesserOfHelperText}
                                                getLesserOfError={getLesserOfError}
                                                nameExistErrors={nameExistErrors}
                                                nameValidatePending={nameValidatePending}
                                                gpciLocalityData={gpciLocalityData}
                                                feeScheduleLabels={feeScheduleLabels}
                                                fetchItemsBasedOnTargetClient={fetchItemsBasedOnTargetClient}
                                                feeScheduleLabelsValidate={feeScheduleLabelsValidate}
                                                getFeeScheduleLabelsFromTargetClientsPending={getFeeScheduleLabelsFromTargetClientsPending}
                                            />
                                         : steps[activeStep]?.label === copyContractConstants.CODESERVICE ? 
                                            <CodeServiceMappingStep
                                                values={values}
                                                codeServicesFromClient={codeServicesFromClient}
                                                handleTargetCodeServiceChange={handleTargetCodeServiceChange}
                                                handleNewCodeServicceeChange={handleNewCodeServicceeChange}
                                                touched={touched}
                                                errors={errors}
                                                getCodeServiceHelperText={getCodeServiceHelperText}
                                                getCodeServiceError={getCodeServiceError}
                                                setFieldValue={setFieldValue}
                                                handleBlur={handleBlur}
                                                classes={classes}
                                            />
                                         : steps[activeStep]?.label === copyContractConstants.NPIROLE ? 
                                            <NPIRoleMappingStep
                                                values={values}
                                                npiRolesFromClient={npiRolesFromClient}
                                                handleTargetNPIRoleChange={handleTargetNPIRoleChange}
                                                handleNewNPIRoleChange={handleNewNPIRoleChange}
                                                touched={touched}
                                                errors={errors}
                                                getNPIRoleHelperText={getNPIRoleHelperText}
                                                getNPIRoleError={getNPIRoleError}
                                                setFieldValue={setFieldValue}
                                                handleBlur={handleBlur}
                                                classes={classes}
                                            />
                                         : steps[activeStep]?.label === copyContractConstants.PROVIDERROLE ?
                                            <ProviderRoleMappingStep
                                                values={values}
                                                touched={touched}
                                                errors={errors}
                                                classes={classes}
                                                isCopyNewName={isCopyNewName}
                                                handleCopyModeToggle={handleCopyModeToggle}
                                                setFieldValue={setFieldValue}
                                                targetClient={targetClient}
                                                handleTargetProviderRoleFeeScheduleChange={handleTargetProviderRoleFeeScheduleChange}
                                                handleTargetProviderRoleLabelChange={handleTargetProviderRoleLabelChange}
                                                handleNewProviderRoleFeeScheduleChange={handleNewProviderRoleFeeScheduleChange}
                                                handleBlur={handleBlur}
                                                getProviderRoleHelperText={getProviderRoleHelperText}
                                                getProviderRoleError={getProviderRoleError}
                                                nameExistErrors={nameExistErrors}
                                                nameValidatePending={nameValidatePending}
                                                gpciLocalityData={gpciLocalityData}
                                                feeScheduleLabels={feeScheduleLabels}
                                                fetchItemsBasedOnTargetClient={fetchItemsBasedOnTargetClient}
                                                feeScheduleLabelsValidate={feeScheduleLabelsValidate}
                                                getFeeScheduleLabelsFromTargetClientsPending={getFeeScheduleLabelsFromTargetClientsPending}
                                            />
                                         : <></>}
                                    </div>
                            </DialogContent>
                            <DialogActions>
                                <Grid container className='px-3' justifyContent="flex-end">
                                    {renderActionButtons(handleSubmit, values)}
                                </Grid>
                            </DialogActions>
                        </form>
                    )}
                </Formik>
            </Loadable>
        </Dialog>
    );
}